/**
 * Symbole als Inline-SVG.
 *
 * Eine Symbolschrift oder ein Paket wäre für sechs Zeichen mehr Gewicht als
 * Nutzen -- und ein externer Verweis würde später die Offline-Hülle des
 * Service Workers unterlaufen.
 */

interface IconProps {
  size?: number
  className?: string
}

function Svg({
  size = 22,
  className,
  gefuellt,
  children,
}: IconProps & {
  /**
   * Gefüllt statt gestrichelt.
   *
   * **Aus dem Betrieb:** „die Symbole (nur an einen, an alle): etwas
   * prägnanter (farblich, fett) - das Schloßsymbol war prägnanter." Ein
   * Strichbild bei 13 Punkten ist ein paar Haare; eine gefüllte Fläche hat
   * dasselbe Gewicht wie das Emoji, das dort vorher stand.
   */
  gefuellt?: boolean
  children: React.ReactNode
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={gefuellt === true ? 'currentColor' : 'none'}
      stroke={gefuellt === true ? 'none' : 'currentColor'}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  )
}

/** Die Tastatur des Geräts -- der Weg zu jedem Zeichen, das die Liste nicht hat. */
export const TastaturIcon = (props: IconProps) => (
  <Svg {...props}>
    <rect x="2" y="6" width="20" height="12" rx="2" />
    <path d="M6 10h.01" />
    <path d="M10 10h.01" />
    <path d="M14 10h.01" />
    <path d="M18 10h.01" />
    <path d="M8 14h8" />
  </Svg>
)

export const SmileyIcon = (props: IconProps) => (
  <Svg {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 14.5a4.5 4.5 0 0 0 7 0" />
    <path d="M9 9.5h.01" />
    <path d="M15 9.5h.01" />
  </Svg>
)

/** Eine Filmklappe -- steht für ein Video, wo kein Standbild da ist. */
export const VideoIcon = (props: IconProps) => (
  <Svg {...props}>
    <rect x="2" y="5" width="14" height="14" rx="2" />
    <path d="m16 10 6-3v10l-6-3Z" />
  </Svg>
)

export const PhotoIcon = (props: IconProps) => (
  <Svg {...props}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <circle cx="8.5" cy="9.5" r="1.5" />
    <path d="m21 16-5-5-6 6-3-3-4 4" />
  </Svg>
)

/**
 * Eine Kamera — aufnehmen statt aussuchen.
 *
 * Getrennt vom Bildsymbol, weil es zwei verschiedene Wege sind: das eine
 * öffnet die Foto-App, das andere die Kamera. Auf einem Telefon ist das der
 * Unterschied zwischen „was ich schon habe" und „was gerade vor mir ist".
 */
export const KameraIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M3 8a2 2 0 0 1 2-2h2.5l1.5-2h6l1.5 2H19a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
    <circle cx="12" cy="12.5" r="3.5" />
  </Svg>
)

/**
 * Ein Kasten mit dem Wort darin -- der Weg zur GIF-Auswahl.
 *
 * Buchstaben und kein Sinnbild: „GIF" ist der Name der Sache und in jedem
 * Messenger derselbe. Ein gezeichnetes Symbol dafür müsste man erst lernen.
 */
export const GifIcon = (props: IconProps) => (
  <Svg {...props}>
    <rect x="2" y="5" width="20" height="14" rx="3" />
    <text
      x="12"
      y="15.5"
      textAnchor="middle"
      fontSize="8"
      fontWeight="700"
      fill="currentColor"
      stroke="none"
      fontFamily="system-ui, -apple-system, sans-serif"
    >
      GIF
    </text>
  </Svg>
)

/**
 * Eine Wellenform — der Weg zur Sprachnachricht, und was dabei herauskommt.
 *
 * **Hieß einmal `MikroIcon` und war ein Mikrofon.** Aus dem Betrieb: „das
 * Symbol sollten wir übrigens ersetzen. statt Mikrofon eine Wellenform." Ein
 * Mikrofon ist das *Gerät*; die Wellenform ist die **Sache** — und dieselbe
 * drei Stellen weiter: am Anhang ohne Standbild und in der Speicherübersicht
 * unter „Sprache" stand bisher ein Aufnahmegerät, wo eine Tonspur gemeint war.
 *
 * Fünf Balken um die Mitte, außen kurz, innen lang: bei 16 Punkten bleiben die
 * Abstände offen, bei 20 trägt es. Runde Enden hebt `Svg` an, sie ragen je
 * einen Punkt über die Werte hinaus — deshalb endet der höchste Balken bei 4
 * und 20 und nicht am Rand.
 */
export const WelleIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M4 10v4" />
    <path d="M8 6v12" />
    <path d="M12 4v16" />
    <path d="M16 7v10" />
    <path d="M20 10v4" />
  </Svg>
)

/**
 * Eine Glocke — dieser Mensch bekommt Benachrichtigungen (#32).
 *
 * **Die Glocke und nicht das Auge**, obwohl aus dem Betrieb „z.B. ein Auge"
 * kam: das durchgestrichene Auge gibt es hier schon und steht für eine
 * zurückgezogene Reise. Zwei Bedeutungen für ein Bild sind dasselbe Problem
 * wie zwei Wörter für eine Sache.
 */
export const GlockeIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M18 15v-4a6 6 0 1 0-12 0v4l-1.5 2.5h15L18 15Z" />
    <path d="M10 20a2 2 0 0 0 4 0" />
  </Svg>
)

/** Dieselbe Glocke, durchgestrichen — erreichbar, aber stummgeschaltet. */
export const GlockeAusIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M18 15v-4a6 6 0 1 0-12 0v4l-1.5 2.5h15L18 15Z" />
    <path d="M10 20a2 2 0 0 0 4 0" />
    <path d="M4 3.5 20 20.5" />
  </Svg>
)

/**
 * Ein Mond — stummgeschaltet, und es kämen ohnehin keine Benachrichtigungen.
 *
 * Der Mond sagt „nicht stören" und nicht „abgeschaltet": wer kein Gerät
 * angemeldet hat, verpasst nichts, was er sonst bekommen hätte.
 */
export const MondIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z" />
  </Svg>
)

/** Ein gefülltes Quadrat — hält die laufende Aufnahme an. */
export const StopIcon = (props: IconProps) => (
  <Svg {...props}>
    <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" />
  </Svg>
)

/** Ein Lupenglas — die Ortssuche auf der Karte. */
export const LupeIcon = (props: IconProps) => (
  <Svg {...props}>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </Svg>
)

/** Ein Herz — auf einer Karte im Stapel zählt es die Reaktionen. */
export const HeartIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M12 20s-7-4.4-7-9.2A4 4 0 0 1 12 8a4 4 0 0 1 7 2.8C19 15.6 12 20 12 20Z" />
  </Svg>
)

export const XIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </Svg>
)

export const ChevronLeftIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="m15 18-6-6 6-6" />
  </Svg>
)

export const ChevronRightIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="m9 18 6-6-6-6" />
  </Svg>
)

/*
 * Pause und Wiedergabe sind gefuellte Zeichen, keine Umrisse.
 *
 * Der gemeinsame Rahmen zeichnet nur Linien (`fill="none"`). Zwei Balken als
 * Umriss werden daraus haarfeine hohle Striche -- bei 16 Pixel in Grau sieht
 * man sie schlicht nicht. Genau so ist es im Betrieb passiert: der Knopf war
 * da und trotzdem unsichtbar.
 */
export const PauseIcon = ({ size = 22, className }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
    focusable="false"
  >
    <rect x="7" y="5" width="3.5" height="14" rx="1" />
    <rect x="13.5" y="5" width="3.5" height="14" rx="1" />
  </svg>
)

export const PlayIcon = ({ size = 22, className }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
    focusable="false"
  >
    <path d="M8 5.5v13l11-6.5z" />
  </svg>
)

export const ChevronDownIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="m6 9 6 6 6-6" />
  </Svg>
)

export const ArchiveIcon = (props: IconProps) => (
  <Svg {...props}>
    <rect x="3" y="4" width="18" height="4" rx="1" />
    <path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8" />
    <path d="M10 12h4" />
  </Svg>
)

/**
 * Ein Haken und zwei Haken -- gelesen oder noch nicht.
 *
 * Zwei getrennte Zeichen statt eines mit Zustand, weil sie Verschiedenes
 * sagen: **ein** Haken heißt "steht für sie bereit", **zwei** heißt "sie hat
 * es gelesen". Ein "zugestellt" im Sinne eines Messengers gibt es hier nicht
 * und kann es nicht geben: ohne dauerhafte Verbindung weiß der Server nicht,
 * ob ein Gerät die Nachricht schon geholt hat. Der einzelne Haken behauptet
 * deshalb bewusst nur, dass sie abholbereit liegt.
 */
export const CheckIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="m4 12 5 5L20 6" />
  </Svg>
)

export const DoubleCheckIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="m2 12 5 5L17 7" />
    <path d="m11 17 1.5 1.5L23 8" />
  </Svg>
)

/**
 * Durchgestrichenes Auge -- steht für eine zurückgezogene Reise.
 *
 * Bewusst ein anderes Bild als der Archivkasten: abgeschlossen heißt "es
 * kommt nichts mehr dazu", zurückgezogen heißt "niemand sieht es mehr außer
 * dir". Zwei Stufen, zwei Zeichen.
 */
export const HiddenIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M3 3l18 18" />
    <path d="M10.6 5.2A9.8 9.8 0 0 1 12 5c5 0 9 4.5 9 7a11 11 0 0 1-2.5 3.4" />
    <path d="M6.2 6.9A11.3 11.3 0 0 0 3 12c0 2.5 4 7 9 7a9.6 9.6 0 0 0 4-.9" />
    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
  </Svg>
)

/**
 * Drei Zeilen -- „hier steht ein Text".
 *
 * Steht in der Spot-Liste hinter dem Namen. Ohne dieses Zeichen sieht eine
 * Zeile mit Text aus wie eine ohne, und niemand tippt darauf.
 */
export const NotizIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M4 6h16" />
    <path d="M4 12h16" />
    <path d="M4 18h10" />
  </Svg>
)

export const PencilIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </Svg>
)

/**
 * Gebogener Pfeil nach links -- „darauf antworten".
 *
 * Dieselbe Form, die Messenger dafür seit Jahren verwenden: der Pfeil zeigt
 * zurück auf das, worauf man sich bezieht. Ein Sprechblasensymbol wäre hier
 * mehrdeutig, denn alles ringsum ist schon eine Sprechblase.
 */
export const ReplyIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M9 17 4 12l5-5" />
    <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
  </Svg>
)

/** Durchgestrichene Glocke -- steht für eine stummgeschaltete Reise. */
export const MuteIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    <path d="M18 8a6 6 0 0 0-9.3-5" />
    <path d="M6.3 6.3A6 6 0 0 0 6 8c0 7-3 9-3 9h14" />
    <path d="m2 2 20 20" />
  </Svg>
)

export const BackIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M19 12H5" />
    <path d="m12 19-7-7 7-7" />
  </Svg>
)

export const SendIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M22 2 11 13" />
    <path d="M22 2 15 22l-4-9-9-4Z" />
  </Svg>
)

export const PlusIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </Svg>
)

export const InfoIcon = (props: IconProps) => (
  <Svg {...props}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </Svg>
)

export const SettingsIcon = (props: IconProps) => (
  <Svg {...props}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1.08-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
  </Svg>
)

export const TrashIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M3 6h18" />
    <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
    <path d="M19 6v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6" />
  </Svg>
)

/**
 * Eine Person — **eine** Empfängerin, und damit: privat.
 *
 * Aus dem Betrieb: „bisher wurde das Schloss-Symbol als Kennzeichnung für
 * private Nachrichten verwendet. Das deutet aber auf Verschlüsselung hin."
 * Stimmt — und mit einer echten Ende-zu-Ende-Verschlüsselung (#23) würde es
 * geradezu falsch.
 *
 * **Person und Gruppe sind ein Paar**, und der Unterschied ist die Anzahl.
 * Genau das ist die Aussage: einer sieht es, oder alle. Ein Schloss, ein Auge
 * oder ein Megafon daneben wären drei Bildsprachen für eine Frage.
 */
export const PersonIcon = (props: IconProps) => (
  <Svg {...props} gefuellt>
    <circle cx="12" cy="7.5" r="4" />
    <path d="M12 13.5c-4.4 0-8 2.6-8 5.9V21h16v-1.6c0-3.3-3.6-5.9-8-5.9Z" />
  </Svg>
)

/** Mehrere Personen — an alle, die mitlesen. Das Gegenstück zu `PersonIcon`. */
export const GruppeIcon = (props: IconProps) => (
  <Svg {...props} gefuellt>
    <circle cx="9" cy="8" r="3.6" />
    <path d="M9 13.6c-3.9 0-7 2.3-7 5.2V21h14v-2.2c0-2.9-3.1-5.2-7-5.2Z" />
    <circle cx="17.6" cy="9" r="2.7" />
    <path d="M17.6 13.6c-.9 0-1.7.12-2.5.35 1.5 1.3 2.4 3 2.4 4.85V21H23v-2.2c0-2.9-2.4-5.2-5.4-5.2Z" />
  </Svg>
)

/**
 * Ein Pfeil nach oben — der Sendeknopf im Feld.
 *
 * **Und kein Papierflieger.** Der stand vorher da; Apple nimmt im
 * Eingabefeld einen Pfeil, und genau darum ging es bei #8. Der Flieger
 * bleibt als `SendIcon` stehen, falls ihn woanders jemand braucht.
 */
export const PfeilHochIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M12 20V5" />
    <path d="m5 12 7-7 7 7" />
  </Svg>
)

/**
 * Zwei Blätter übereinander — den Wortlaut in die Zwischenablage.
 *
 * Neu mit dem Menü an der Sprechblase (#8): auf iOS unterdrückt
 * `-webkit-touch-callout: none` das Systemmenü, damit das lange Drücken
 * unseres wird -- und damit fiel „Kopieren" weg. Es kommt hier wieder,
 * und ist jetzt auch am Rechner da, wo es das vorher nie gab.
 */
export const KopierenIcon = (props: IconProps) => (
  <Svg {...props}>
    <rect x="9" y="9" width="12" height="12" rx="2" />
    <path d="M5 15V5a2 2 0 0 1 2-2h10" />
  </Svg>
)

/** Eine Stecknadel — der Punkt auf der Karte, an dem etwas war. */
export const PinIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11Z" />
    <circle cx="12" cy="10" r="2.5" />
  </Svg>
)

/**
 * Der eigene Standort — das Fadenkreuz, nicht die Nadel.
 *
 * **Es muss sich von `PinIcon` unterscheiden lassen**, denn beide stehen
 * nebeneinander: „Highlight" setzt einen Punkt, wo man hintippt, „Hier" setzt
 * ihn dorthin, wo das Gerät gerade steht. Eine zweite Nadel hieße zweimal
 * dasselbe. Vier Striche nach außen und ein Punkt in der Mitte sind das
 * Zeichen, das jede Kartenanwendung dafür benutzt — es muss niemand lernen.
 */
export const StandortIcon = (props: IconProps) => (
  <Svg {...props}>
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" />
    <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
  </Svg>
)

/* --- Wetter ---------------------------------------------------------------
 *
 * Acht Zeichen für die acht Lagen aus `lib/wetterlage.ts`, damit die Kachel im
 * Kartenstapel ohne Worte auskommt. Selbst gezeichnet und nicht aus einem
 * Paket: der Grund oben in dieser Datei gilt hier genauso.
 *
 * **Keine Nacht-Zeichen.** Gespeichert wird nur die Lage, nicht Apples
 * `daylight`; ein Mond ließe sich nur aus dem Zeitstempel raten, und das hieße
 * Sonnenstand rechnen für eine Kachel von Daumengröße.
 */

/** Klar — auch „überwiegend klar". */
export const SonneIcon = (props: IconProps) => (
  <Svg {...props}>
    <circle cx="12" cy="12" r="4.5" />
    <path d="M12 2v2" />
    <path d="M12 20v2" />
    <path d="M2 12h2" />
    <path d="M20 12h2" />
    <path d="m4.9 4.9 1.5 1.5" />
    <path d="m17.6 17.6 1.5 1.5" />
    <path d="m19.1 4.9-1.5 1.5" />
    <path d="m6.4 17.6-1.5 1.5" />
  </Svg>
)

/** Teils bewölkt: die Sonne lugt hinter der Wolke hervor. */
export const TeilsIcon = (props: IconProps) => (
  <Svg {...props}>
    <circle cx="8" cy="8" r="3" />
    <path d="M8 1.5V3" />
    <path d="M1.5 8H3" />
    <path d="m3.4 3.4 1 1" />
    <path d="m12.6 3.4-1 1" />
    <path d="M17.5 20H8a4.5 4.5 0 0 1 0-9 5 5 0 0 1 9.5 1.5 3.75 3.75 0 0 1 0 7.5Z" />
  </Svg>
)

/** Bewölkt. */
export const WolkeIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M17.5 19H7a4.5 4.5 0 0 1 0-9 5.5 5.5 0 0 1 10.4 1.5 3.75 3.75 0 0 1 .1 7.5Z" />
  </Svg>
)

/** Regen — Niesel, Regen, starker Regen. */
export const RegenIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M17.5 15H7a4.5 4.5 0 0 1 0-9 5.5 5.5 0 0 1 10.4 1.5 3.75 3.75 0 0 1 .1 7.5Z" />
    <path d="M8 18.5 7 21" />
    <path d="M12.5 18.5 11.5 21" />
    <path d="M17 18.5 16 21" />
  </Svg>
)

/** Schnee — und Schneeregen und Hagel. */
export const SchneeIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M17.5 15H7a4.5 4.5 0 0 1 0-9 5.5 5.5 0 0 1 10.4 1.5 3.75 3.75 0 0 1 .1 7.5Z" />
    <path d="M8 19h.01" />
    <path d="M12 21h.01" />
    <path d="M16 19h.01" />
    <path d="M10 21.5h.01" />
    <path d="M14 17.5h.01" />
  </Svg>
)

/** Gewitter. */
export const GewitterIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M17.5 14H7a4.5 4.5 0 0 1 0-9 5.5 5.5 0 0 1 10.4 1.5 3.75 3.75 0 0 1 .1 7.5Z" />
    <path d="m13 16-3 4h4l-3 4" />
  </Svg>
)

/** Nebel — und Dunst. */
export const NebelIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M17.5 13H7a4.5 4.5 0 0 1 0-9 5.5 5.5 0 0 1 10.4 1.5 3.75 3.75 0 0 1 .1 7.5Z" />
    <path d="M5 17h9" />
    <path d="M17 17h2" />
    <path d="M7 21h10" />
  </Svg>
)

/** Wind — windig und frischer Wind. */
export const WindIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M3 8h10a3 3 0 1 0-3-3" />
    <path d="M3 12h14a3 3 0 1 1-3 3" />
    <path d="M3 16h7a2.5 2.5 0 1 1-2.5 2.5" />
  </Svg>
)

/* --- Umgebung -------------------------------------------------------------
 *
 * Sieben Zeichen fuer die sieben Schubladen der Umgebungssuche
 * (`server/src/Umgebung.php`). Selbst gezeichnet, aus demselben Grund wie
 * oben -- und hier kommt einer dazu: Apple zeigt in seiner eigenen Liste
 * farbige Symbole, und die gehoeren Apple. Diese hier sind Striche in der
 * Schriftfarbe wie jedes andere Zeichen in dieser Anwendung.
 */

/** Essen & Trinken: Messer und Gabel. */
export const BesteckIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M6 3v7a2 2 0 0 0 4 0V3" />
    <path d="M8 10v11" />
    <path d="M17 3c-1.5 1.5-2 3.5-2 5.5S15.5 12 17 12s2-1.5 2-3.5S18.5 4.5 17 3Z" />
    <path d="M17 12v9" />
  </Svg>
)

/** Bar & Abend: ein Glas. */
export const GlasIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M5 4h14l-7 8Z" />
    <path d="M12 12v7" />
    <path d="M8 19h8" />
  </Svg>
)

/** Kultur: ein Haus mit Saeulen. */
export const SaeulenIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="m3 9 9-5 9 5" />
    <path d="M5 9v8" />
    <path d="M10 9v8" />
    <path d="M14 9v8" />
    <path d="M19 9v8" />
    <path d="M3 20h18" />
  </Svg>
)

/** Sehenswuerdigkeit: ein Stern -- wie in Apples eigener Liste. */
export const SternIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="m12 3 2.6 5.6 6.4.8-4.7 4.3 1.2 6.3-5.5-3-5.5 3 1.2-6.3L3 9.4l6.4-.8Z" />
  </Svg>
)

/** Natur & Aussicht: ein Berg. */
export const BergIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="m2 19 6.5-11 4 6.5 2.5-4L21 19Z" />
    <path d="m6.5 11.5 2 1.5" />
  </Svg>
)

/** Uebernachtung: ein Bett. */
export const BettIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M3 18v-9" />
    <path d="M3 13h18v5" />
    <path d="M21 18v-4a3 3 0 0 0-3-3h-7v2" />
    <circle cx="7" cy="10.5" r="1.5" />
  </Svg>
)

/** Markt & Laden: eine Einkaufstasche. */
export const TascheIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M5 8h14l-1 12H6Z" />
    <path d="M9 11V7a3 3 0 0 1 6 0v4" />
  </Svg>
)

/** Camping & Stellplatz: ein Zelt. */
export const ZeltIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M12 4 3 20h18Z" />
    <path d="M12 4v16" />
    <path d="m8.5 20 3.5-6 3.5 6" />
  </Svg>
)
